import { useFormatter } from '@api/useFormatter'
import { Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Tooltip } from 'antd'
import classNames from 'classnames'
import { uniqueId } from 'lodash'
import React from 'react'

type OrderProductsProps = {
  order?: Gravitate.Order
  wrap?: 'wrap' | 'nowrap' | 'wrap-reverse'
  showAlerts?: boolean
}

const errorAlerts = ['no fit', 'runout', 'empty']
const warningAlerts = ['potential no fit', 'potential runout']

export function OrderProducts({ order, wrap = 'wrap', showAlerts = false }: OrderProductsProps) {
  if (!order) return null
  const ratios = order.product_ratios
  if (!ratios || ratios.length === 0) return null
  const multipleProducts = ratios.length > 1

  const productTypeMap: Record<string, string> = {}
  order.window_alert?.forEach((item) => {
    productTypeMap[item.product] = item.type
  })

  return (
    <div className='flex' style={{ flexWrap: wrap }}>
      {ratios.map((r) => {
        const key = uniqueId()
        let severity
        if (showAlerts && errorAlerts.includes(productTypeMap[r.name])) {
          severity = 'error'
        } else if (showAlerts && warningAlerts.includes(productTypeMap[r.name])) {
          severity = 'warning'
        }
        return <ProductPill key={key} multipleProducts={multipleProducts} ratio={r} severity={severity} />
      })}
    </div>
  )
}

type ProductPillProps = {
  multipleProducts: boolean
  ratio: Gravitate.OrderProductRatio
  severity?: 'error' | 'warning'
}

export function ProductPill({ ratio, severity }: ProductPillProps) {
  if (!ratio) return null
  const { formatVolume } = useFormatter()
  const formattedValue = formatVolume(ratio.volume, true)?.toUpperCase()

  let circleBg
  switch (severity) {
    case 'error':
      circleBg = 'error'
      break
    case 'warning':
      circleBg = 'warning'
      break
    default:
      circleBg = 'default'
  }

  if (!severity) {
    return (
      <Tooltip placement='top' title={formattedValue}>
        <div>
          <Horizontal alignItems='stretch' className='mr-2 mb-1 ' style={{ overflow: 'visible' }}>
            {ratio.amount && (
              <Vertical
                verticalCenter
                borderRadius='6px 0 0 6px'
                className='px-1'
                style={{
                  minWidth: 16,
                  background: 'var(--gray-700)',
                  height: '100%',
                  border: 'solid 1px var(--gray-700)',
                }}
              >
                <Texto
                  style={{ color: 'var(--gray-100)' }}
                  weight='bold'
                  align='center'
                  category='label'
                  appearance={circleBg}
                >
                  {ratio.amount}
                </Texto>
              </Vertical>
            )}

            <Vertical
              verticalCenter
              className='px-1'
              style={{
                fontWeight: 600,
                textTransform: 'uppercase',
                background: 'var(--bg-2)',
                borderRadius: '0 6px 6px 0',
                height: '100%',
                border: 'solid 1px var(--gray-300)',
              }}
            >
              <Texto category='label' appearance={circleBg}>
                {ratio.name?.substring(0, 8)}
              </Texto>
            </Vertical>
          </Horizontal>
        </div>
      </Tooltip>
    )
  }
  return (
    <Tooltip placement='top' title={formattedValue}>
      <div>
        <Horizontal className='mr-2' style={{ overflow: 'visible' }}>
          <Vertical
            verticalCenter
            border='all'
            borderRadius='6px 0 0 6px'
            className='px-1'
            bg={circleBg}
            style={{ ...styles.circle }}
          >
            <Texto weight='bold' category='label' appearance={circleBg}>
              {ratio.amount}
            </Texto>
          </Vertical>

          <Vertical
            verticalCenter
            className='px-1'
            style={{
              fontSize: '0.9em',
              height: 20,
              fontWeight: 600,
              textTransform: 'uppercase',
              background: 'var(--bg-2)',
              borderRadius: '0 6px 6px 0',
              border: 'solid 1px var(--gray-300)',
            }}
          >
            {ratio.name?.substring(0, 8)}
          </Vertical>
        </Horizontal>
      </div>
    </Tooltip>
  )
}

const styles = {
  circle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 20,
    border: '1px solid var(--gray-300)',
  },
}
